import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { fetchAllCurrencies, formatCurrencyOptions, detectUserCurrency } from '../../../services/currencyService';

const CompanySettings = ({ onChange }) => {
  const [formData, setFormData] = useState({
    companyName: 'AccountingPro Inc.',
    legalName: 'AccountingPro Incorporated',
    taxId: '12-3456789',
    email: 'contact@accountingpro.com',
    phone: '+1 (555) 123-4567',
    website: 'www.accountingpro.com',
    address: '123 Business Street',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    country: 'US',
    fiscalYearStart: '01',
    currency: 'USD',
    timezone: 'America/New_York'
  });

  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);

  useEffect(() => {
    loadCurrencies();
  }, []);

  const loadCurrencies = async () => {
    setLoadingCurrencies(true);
    try {
      // Fetch all currencies from API
      const currencies = await fetchAllCurrencies();
      const options = formatCurrencyOptions(currencies);
      setCurrencyOptions(options);

      // Auto-detect user's currency if not already set
      if (formData?.currency === 'USD') {
        const detectedCurrency = await detectUserCurrency();
        if (detectedCurrency && detectedCurrency !== 'USD') {
          setFormData(prev => ({ ...prev, currency: detectedCurrency }));
        }
      }
    } catch (error) {
      console.error('Error loading currencies:', error);
    } finally {
      setLoadingCurrencies(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    onChange?.();
  };

  const monthOptions = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' }
  ];

  return (
    <div className="space-y-8">
      {/* Business Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Company Name"
            value={formData?.companyName}
            onChange={(e) => handleChange('companyName', e?.target?.value)}
            required
          />
          <Input
            label="Legal Name"
            value={formData?.legalName}
            onChange={(e) => handleChange('legalName', e?.target?.value)}
          />
          <Input
            label="Tax ID / EIN"
            value={formData?.taxId}
            onChange={(e) => handleChange('taxId', e?.target?.value)}
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            required
          />
          <Input
            label="Phone"
            type="tel"
            value={formData?.phone}
            onChange={(e) => handleChange('phone', e?.target?.value)}
          />
          <Input
            label="Website"
            type="url"
            value={formData?.website}
            onChange={(e) => handleChange('website', e?.target?.value)}
          />
        </div>
      </div>
      {/* Address */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Address</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input
              label="Street Address"
              value={formData?.address}
              onChange={(e) => handleChange('address', e?.target?.value)}
            />
          </div>
          <Input
            label="City"
            value={formData?.city}
            onChange={(e) => handleChange('city', e?.target?.value)}
          />
          <Input
            label="State / Province"
            value={formData?.state}
            onChange={(e) => handleChange('state', e?.target?.value)}
          />
          <Input
            label="ZIP / Postal Code"
            value={formData?.zipCode}
            onChange={(e) => handleChange('zipCode', e?.target?.value)}
          />
          <Input
            label="Country"
            value={formData?.country}
            onChange={(e) => handleChange('country', e?.target?.value)}
          />
        </div>
      </div>
      {/* Financial Settings */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Select
            label="Fiscal Year Start"
            value={formData?.fiscalYearStart}
            onChange={(value) => handleChange('fiscalYearStart', value)}
            options={monthOptions}
          />
          <Select
            label="Default Currency"
            value={formData?.currency}
            onChange={(value) => handleChange('currency', value)}
            options={currencyOptions}
            disabled={loadingCurrencies}
          />
          <Select
            label="Timezone"
            value={formData?.timezone}
            onChange={(value) => handleChange('timezone', value)}
            options={timezoneOptions}
          />
        </div>
        {loadingCurrencies && (
          <p className="text-sm text-gray-500 mt-2">Loading currencies...</p>
        )}
      </div>
    </div>
  );
};

export default CompanySettings;